import { useCallback, useEffect, useRef, useState } from 'react'

import { useTypedDispatch } from '../..'
import { DELAY_NOTIFICATION_HANDLE_TIMEOUT_MS } from '../../constants'
import {
  addToHandledNotifications,
  DelayNotificationResolution,
  markNotificationHandled,
  persistDelayNotificationHandledAction,
} from '../../reducers/delayNotificationSlice'

type Options = {
  notificationKey: string
  autoDisappear: boolean
  resolution?: DelayNotificationResolution
}

export const useDelayNotificationAutoDismiss = ({
  notificationKey,
  autoDisappear,
  resolution,
}: Options) => {
  const dispatch = useTypedDispatch()
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const wasTransitioned = useRef(false)
  const wasMarkedHandled = useRef(false)
  const [isGone, setIsGone] = useState(false)

  const startExit = useCallback(() => {
    setIsGone(true)
    wasMarkedHandled.current = true
    dispatch(markNotificationHandled({ key: notificationKey }))
  }, [dispatch, notificationKey])

  useEffect(() => {
    if (autoDisappear) {
      dispatch(addToHandledNotifications({ key: notificationKey }))
      wasTransitioned.current = true
      if (resolution) {
        dispatch(persistDelayNotificationHandledAction({ notificationKey, resolution }))
          .unwrap()
          .catch((error) => console.error('Failed to save delay notification:', error))
      }
      timerRef.current = setTimeout(startExit, DELAY_NOTIFICATION_HANDLE_TIMEOUT_MS)
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
      if (wasTransitioned.current && !wasMarkedHandled.current) {
        wasMarkedHandled.current = true
        dispatch(markNotificationHandled({ key: notificationKey }))
      }
    }
  }, [autoDisappear, dispatch, notificationKey, resolution, startExit])

  return {
    isGone,
    startExit,
    wasTransitioned: wasTransitioned.current,
  }
}
